import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  WeeklyForecast,
  Day,
  Temperature,
  ToggleButton,
  City,
  WeatherBox,
  WeeklyForecastButton,
  Wind,
} from "./styled";
import WeatherIcon from "./WIcon";
import WeatherModal from "./Modal";

const WeeklyForecastModal = ({ modalOpen, setModalOpen, forecast }) => {
  const days = [];

  const numDays = 7;
  const numHoursPerDay = 24;
  const numHours = numDays * numHoursPerDay;

  for (let i = 0; i < numHours; i += numHoursPerDay) {
    const day = {
      date: forecast?.hourly?.time[i],
      temperature: forecast?.hourly?.temperature_2m[i],
      windSpeed: forecast?.daily?.windspeed_10m_max,
      weatherCode: forecast?.daily?.weathercode,
    };

    days.push(day);
  }

  return (
    modalOpen && (
      <WeatherModal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <WeeklyForecast>
          {days.map((day, index) => (
            <Day key={index}>
              <h3>
                {new Date(day?.date).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </h3>
              <WeatherIcon code={day?.weatherCode[index]} />
              <Temperature>{day?.temperature}°C</Temperature>
              <Wind>Wind speed: </Wind>
              <h4>{day.windSpeed[index]} m/s</h4>
            </Day>
          ))}
        </WeeklyForecast>
      </WeatherModal>
    )
  );
};

const getCity = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
    );
    const data = await response.json();
    const city = data.address.city || data.address.suburb || data.address.town;
    return city;
  } catch (error) {
    console.error(error);
  }
};

const getTimezone = async (latitude, longitude) => {
  const tzLookup = require("tz-lookup");

  const timezone = tzLookup(latitude, longitude);
  return timezone;
};

const getWeeklyForecast = async (latitude, longitude) => {
  const currentDate = new Date();
  const endDate = new Date();
  endDate.setDate(currentDate.getDate() + 7);

  const startDateString = currentDate.toISOString().split("T")[0];
  const endDateString = endDate.toISOString().split("T")[0];

  const timezone = await getTimezone(latitude, longitude);

  const apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&hourly=temperature_2m&start_date=${startDateString}&end_date=${endDateString}&daily=windspeed_10m_max,weathercode&timezone=${timezone}`;
  const response = await fetch(apiUrl);
  const data = await response.json();

  return data;
};

const Weather = () => {
  const [weather, setWeather] = useState({});
  const [forecast, setForecast] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState("C");

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      const apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&hourly=temperature_2m&current_weather=true`;
      const response = await fetch(apiUrl);
      const data = await response.json();

      const currentTemperature = data.current_weather.temperature;
      const windSpeed = data.current_weather.windspeed;
      const weatherCode = data.current_weather.weathercode;

      const city = await getCity(latitude, longitude);

      setWeather({
        latitude,
        longitude,
        city,
        code: weatherCode,
        temperature: currentTemperature,
        weather: data.hourly,
        wind: {
          speed: windSpeed,
        },
      });
    });
  }, []);

  const toggleTemperatureUnit = () => {
    if (temperatureUnit === "F") {
      setTemperatureUnit("C");
    } else {
      setTemperatureUnit("F");
    }
  };

  const temperature = weather.temperature;
  const fahrenheit = (temperature * 9) / 5 + 32;
  const temperatureToDisplay =
    temperatureUnit === "F" ? fahrenheit : temperature;

  const handleShowWeeklyForecast = async () => {
    const { longitude, latitude } = weather;
    const data = await getWeeklyForecast(latitude, longitude);
    setForecast(data);
    setModalOpen(true);
  };

  return (
    <WeatherBox>
      {temperature && (
        <>
          <City>{weather.city}</City>
          <WeatherIcon code={weather.code} />
          <Temperature>
            {temperatureToDisplay}
            <ToggleButton onClick={toggleTemperatureUnit}>
              {temperatureUnit === "F" ? "°F" : "°C"}
            </ToggleButton>
            <Wind>Wind: {weather.wind.speed} m/s</Wind>
          </Temperature>
          <WeeklyForecastButton onClick={handleShowWeeklyForecast}>
            🌡️ Weekly Forecast
          </WeeklyForecastButton>
        </>
      )}
      {forecast && (
        <WeeklyForecastModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          forecast={forecast}
        />
      )}
    </WeatherBox>
  );
};

Weather.propTypes = {
  data: PropTypes.object,
};

export default Weather;
